import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import {
  buildKpiColumns,
  buildKpiFullBriefing,
  buildKpiRow,
  type XFactorKpiCategoryDetail,
  type XFactorKpiColumn,
  type XFactorKpiFullBriefing,
  type XFactorKpiRowModel,
} from "../../Pages/TempViewsRendering/lib/deriveKpi";
import { get, post } from "../../Services/ApiRequest";
import { API_URLS } from "../../Utils/Constants/APIConstants";

/**
 * KPI Metrics Briefing reads + generation (render/kpi, kpi/scores/latest,
 * kpi/generate). Several surfaces read different accounts/dates at once
 * (Account Synopsis strip, Hawkeye bubble modals, the portfolio matrix), so
 * each read is cached under its own request key rather than one shared slot
 * — otherwise one account's response would overwrite another's.
 */

export type XFactorKpiMatrixGranularity =
  | "weekly"
  | "monthly"
  | "quarterly"
  | "yearly";

export type XFactorKpiScoresLatestCategory = {
  category_id: string;
  name?: string | null;
  score: number | null;
  observations?: number | null;
};

export type XFactorKpiScoresLatest = {
  date: string;
  overall_score: number | null;
  categories: XFactorKpiScoresLatestCategory[];
  risk_score: number | null;
  opportunity_score: number | null;
  coverage: { scored_categories: number; enabled_categories: number } | null;
  briefing_available: boolean;
  previous_date: string | null;
  previous_overall_score: number | null;
  available_dates: string[];
};

type RequestMeta = {
  loading: boolean;
  error: string | null;
  /** Latest in-flight request for this key — older responses are ignored. */
  requestId: string | null;
};

export type KpiAccountBriefingEntry = RequestMeta & {
  data: XFactorKpiCategoryDetail;
  date: string | null;
};

export type KpiFullBriefingEntry = RequestMeta & {
  briefing: XFactorKpiFullBriefing | null;
};

export type KpiMatrixEntry = RequestMeta & {
  columns: XFactorKpiColumn[];
  rowsByAccountId: Record<string, XFactorKpiRowModel>;
  date: string | null;
};

export type KpiScoresLatestEntry = RequestMeta & {
  data: XFactorKpiScoresLatest | null;
  notFound: boolean;
};

interface XFactorKpiState {
  accountBriefings: Record<string, KpiAccountBriefingEntry>;
  fullBriefings: Record<string, KpiFullBriefingEntry>;
  matrices: Record<string, KpiMatrixEntry>;
  scoresLatest: Record<string, KpiScoresLatestEntry>;
  generate: {
    /** Account whose generation call is in flight — keyed so a matrix of
     * per-row buttons can show a spinner on just the row that was clicked. */
    generatingAccountId: string | null;
    error: string | null;
  };
}

const initialState: XFactorKpiState = {
  accountBriefings: {},
  fullBriefings: {},
  matrices: {},
  scoresLatest: {},
  generate: { generatingAccountId: null, error: null },
};

const unwrap = (res: any) => res?.content ?? res;

// ---------------------------------------------------------------------------
// Request keys — hooks select their entry with the same key the thunk writes.
// ---------------------------------------------------------------------------

type AccountBriefingArgs = {
  accountId: string;
  datasetKind: string;
  measurementDate?: string | null;
};

type MatrixArgs = {
  accountIds: string[];
  datasetKind: string;
  measurementDate?: string | null;
  granularity?: XFactorKpiMatrixGranularity | null;
  period?: string | null;
};

type ScoresLatestArgs = { accountId: string; datasetKind: string };

export const kpiAccountBriefingKey = (args: AccountBriefingArgs): string =>
  [args.accountId, args.datasetKind, args.measurementDate ?? ""].join("|");

export const kpiMatrixKey = (args: MatrixArgs): string =>
  [
    args.accountIds.join(","),
    args.datasetKind,
    args.measurementDate ?? "",
    args.granularity ?? "",
    args.granularity ? (args.period ?? "") : "",
  ].join("|");

export const kpiScoresLatestKey = (args: ScoresLatestArgs): string =>
  [args.accountId, args.datasetKind].join("|");

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

/**
 * Newest live KPI Metrics Briefing "overall" category detail for one account
 * (render/kpi view=category) — the 8-block payload the KPI Situation Card
 * reads. Omit `measurementDate` to let the server resolve the newest briefed
 * date (see docs/xfactor/kpi-briefings.md).
 */
export const fetchKpiAccountBriefing = createAsyncThunk(
  "xfactorKpi/accountBriefing",
  async (args: AccountBriefingArgs, { rejectWithValue }) => {
    try {
      const res = unwrap(
        await post(API_URLS.xfactorKpiAccountBriefings(), {
          account_ids: [args.accountId],
          view: "category",
          category_id: "overall",
          dataset_kind: args.datasetKind,
          ...(args.measurementDate
            ? { measurement_date: args.measurementDate }
            : {}),
        })
      );
      const row = res?.results?.[0];
      const rawBriefing = row?.briefing ?? null;
      // Pass the full wire envelope through — buildKpiSituationCard unwraps
      // native `{ briefing: { overall } }` and external `{ category }`.
      return {
        data: (rawBriefing as XFactorKpiCategoryDetail) ?? null,
        date:
          res?.date ??
          (typeof rawBriefing?.briefing?.measurementDate === "string"
            ? rawBriefing.briefing.measurementDate
            : null) ??
          (typeof rawBriefing?.measurementDate === "string"
            ? rawBriefing.measurementDate
            : null) ??
          null,
        error: row && !row.success ? row.error || null : null,
      };
    } catch (e: any) {
      return rejectWithValue(e?.message || "Failed to load KPI briefing");
    }
  }
);

/**
 * Full account briefing (render/kpi view=full) — every category plus overall
 * in one call, so the category detail modal can switch categories without
 * refetching.
 */
export const fetchKpiFullBriefing = createAsyncThunk(
  "xfactorKpi/fullBriefing",
  async (args: AccountBriefingArgs, { rejectWithValue }) => {
    try {
      const res = unwrap(
        await post(API_URLS.xfactorKpiAccountBriefings(), {
          account_ids: [args.accountId],
          view: "full",
          dataset_kind: args.datasetKind,
          ...(args.measurementDate
            ? { measurement_date: args.measurementDate }
            : {}),
        })
      );
      const row = res?.results?.[0];
      const rawBriefing = row?.briefing?.briefing ?? row?.briefing ?? null;
      return {
        briefing: rawBriefing ? buildKpiFullBriefing(rawBriefing) : null,
        error: row && !row.success ? row.error || null : null,
      };
    } catch (e: any) {
      return rejectWithValue(e?.message || "Failed to load KPI briefing");
    }
  }
);

/**
 * Portfolio-wide KPI matrix rows (render/kpi view=table), one call for every
 * account. `granularity`+`period` narrow resolution to a period window and
 * are ignored server-side when `measurementDate` is set.
 */
export const fetchKpiMatrix = createAsyncThunk(
  "xfactorKpi/matrix",
  async (args: MatrixArgs, { rejectWithValue }) => {
    try {
      const res = unwrap(
        await post(API_URLS.xfactorKpiAccountBriefings(), {
          account_ids: args.accountIds,
          view: "table",
          dataset_kind: args.datasetKind,
          ...(args.measurementDate
            ? { measurement_date: args.measurementDate }
            : {}),
          // Always pass period window so native can fall back to the latest
          // scored date *inside* the selected period when the exact date is empty.
          ...(args.granularity ? { granularity: args.granularity } : {}),
          ...(args.granularity && args.period ? { period: args.period } : {}),
        })
      );
      const columns = buildKpiColumns(res?.columns);
      const rowsByAccountId: Record<string, XFactorKpiRowModel> = {};
      for (const result of res?.results ?? []) {
        const accountId = result?.account_id;
        if (!accountId) continue;
        rowsByAccountId[accountId] = buildKpiRow(
          accountId,
          result?.briefing,
          columns
        );
      }
      return { columns, rowsByAccountId, date: res?.date ?? null };
    } catch (e: any) {
      return rejectWithValue(e?.message || "Failed to load KPI matrix");
    }
  }
);

/**
 * Newest **scored** snapshot for one account (GET kpi/scores/latest) — no AI
 * briefing required. `briefing_available` says whether the drill-down will
 * also succeed for the resolved date.
 */
export const fetchKpiScoresLatest = createAsyncThunk<
  XFactorKpiScoresLatest | null,
  ScoresLatestArgs,
  { rejectValue: { message: string; notFound: boolean } }
>("xfactorKpi/scoresLatest", async (args, { rejectWithValue }) => {
  try {
    const qs = new URLSearchParams({
      account_id: args.accountId,
      dataset_kind: args.datasetKind,
    }).toString();
    const res = unwrap(await get(`${API_URLS.xfactorKpiScoresLatest()}?${qs}`));
    return res ?? null;
  } catch (e: any) {
    let message = e?.message || "Failed to load latest KPI scores";
    let notFound = false;
    try {
      const parsed = JSON.parse(e?.message);
      message = parsed?.message || message;
      notFound = parsed?.status === 404;
    } catch {
      notFound = /404/.test(message);
    }
    return rejectWithValue({ message, notFound });
  }
});

/**
 * POST /kpi/generate — the only way native KPI data gets created for an
 * account/date (no cron, no auto-generate-on-view). Ops-gated server-side
 * (`_require_ops` in routers/xfactor_briefing.py); a non-ops caller's 403
 * comes back through `generate.error` rather than being hidden client-side.
 * KPI generation always runs on Bedrock server-side.
 */
export const generateKpiBriefing = createAsyncThunk(
  "xfactorKpi/generate",
  async (
    args: {
      accountId: string;
      measurementDate: string;
      datasetKind?: string | null;
      forceRefresh?: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      await post(API_URLS.xfactorKpiGenerate(), {
        account_id: args.accountId,
        measurement_date: args.measurementDate,
        ...(args.datasetKind ? { dataset_kind: args.datasetKind } : {}),
        force_refresh: args.forceRefresh ?? false,
      });
      return true;
    } catch (e: any) {
      return rejectWithValue(e?.message || "Failed to generate KPI briefing");
    }
  }
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const pendingMeta = (requestId: string): RequestMeta => ({
  loading: true,
  error: null,
  requestId,
});

const XFactorKpiSlice = createSlice({
  name: "xfactorKpi",
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      // Account briefing (view=category)
      .addCase(fetchKpiAccountBriefing.pending, (state, action) => {
        const key = kpiAccountBriefingKey(action.meta.arg);
        const prev = state.accountBriefings[key];
        state.accountBriefings[key] = {
          data: prev?.data ?? null,
          date: prev?.date ?? null,
          ...pendingMeta(action.meta.requestId),
        };
      })
      .addCase(fetchKpiAccountBriefing.fulfilled, (state, action) => {
        const key = kpiAccountBriefingKey(action.meta.arg);
        if (state.accountBriefings[key]?.requestId !== action.meta.requestId) {
          return;
        }
        state.accountBriefings[key] = {
          ...action.payload,
          loading: false,
          requestId: null,
        };
      })
      .addCase(fetchKpiAccountBriefing.rejected, (state, action) => {
        const key = kpiAccountBriefingKey(action.meta.arg);
        if (state.accountBriefings[key]?.requestId !== action.meta.requestId) {
          return;
        }
        state.accountBriefings[key] = {
          data: null,
          date: null,
          loading: false,
          error: action.payload as string,
          requestId: null,
        };
      })
      // Full briefing (view=full)
      .addCase(fetchKpiFullBriefing.pending, (state, action) => {
        const key = kpiAccountBriefingKey(action.meta.arg);
        state.fullBriefings[key] = {
          briefing: state.fullBriefings[key]?.briefing ?? null,
          ...pendingMeta(action.meta.requestId),
        };
      })
      .addCase(fetchKpiFullBriefing.fulfilled, (state, action) => {
        const key = kpiAccountBriefingKey(action.meta.arg);
        if (state.fullBriefings[key]?.requestId !== action.meta.requestId) {
          return;
        }
        state.fullBriefings[key] = {
          ...action.payload,
          loading: false,
          requestId: null,
        };
      })
      .addCase(fetchKpiFullBriefing.rejected, (state, action) => {
        const key = kpiAccountBriefingKey(action.meta.arg);
        if (state.fullBriefings[key]?.requestId !== action.meta.requestId) {
          return;
        }
        state.fullBriefings[key] = {
          briefing: null,
          loading: false,
          error: action.payload as string,
          requestId: null,
        };
      })
      // Matrix (view=table)
      .addCase(fetchKpiMatrix.pending, (state, action) => {
        const key = kpiMatrixKey(action.meta.arg);
        const prev = state.matrices[key];
        state.matrices[key] = {
          columns: prev?.columns ?? [],
          rowsByAccountId: prev?.rowsByAccountId ?? {},
          date: prev?.date ?? null,
          ...pendingMeta(action.meta.requestId),
        };
      })
      .addCase(fetchKpiMatrix.fulfilled, (state, action) => {
        const key = kpiMatrixKey(action.meta.arg);
        if (state.matrices[key]?.requestId !== action.meta.requestId) return;
        state.matrices[key] = {
          ...action.payload,
          loading: false,
          error: null,
          requestId: null,
        };
      })
      .addCase(fetchKpiMatrix.rejected, (state, action) => {
        const key = kpiMatrixKey(action.meta.arg);
        if (state.matrices[key]?.requestId !== action.meta.requestId) return;
        state.matrices[key] = {
          columns: [],
          rowsByAccountId: {},
          date: null,
          loading: false,
          error: action.payload as string,
          requestId: null,
        };
      })
      // Latest scores
      .addCase(fetchKpiScoresLatest.pending, (state, action) => {
        const key = kpiScoresLatestKey(action.meta.arg);
        state.scoresLatest[key] = {
          data: state.scoresLatest[key]?.data ?? null,
          notFound: false,
          ...pendingMeta(action.meta.requestId),
        };
      })
      .addCase(fetchKpiScoresLatest.fulfilled, (state, action) => {
        const key = kpiScoresLatestKey(action.meta.arg);
        if (state.scoresLatest[key]?.requestId !== action.meta.requestId) {
          return;
        }
        state.scoresLatest[key] = {
          data: action.payload,
          notFound: false,
          loading: false,
          error: null,
          requestId: null,
        };
      })
      .addCase(fetchKpiScoresLatest.rejected, (state, action) => {
        const key = kpiScoresLatestKey(action.meta.arg);
        if (state.scoresLatest[key]?.requestId !== action.meta.requestId) {
          return;
        }
        state.scoresLatest[key] = {
          data: null,
          notFound: action.payload?.notFound ?? false,
          loading: false,
          error:
            action.payload?.message ??
            action.error.message ??
            "Failed to load latest KPI scores",
          requestId: null,
        };
      })
      // Generate
      .addCase(generateKpiBriefing.pending, (state, action) => {
        state.generate = {
          generatingAccountId: action.meta.arg.accountId,
          error: null,
        };
      })
      .addCase(generateKpiBriefing.fulfilled, state => {
        state.generate = { generatingAccountId: null, error: null };
      })
      .addCase(generateKpiBriefing.rejected, (state, action) => {
        state.generate = {
          generatingAccountId: null,
          error: action.payload as string,
        };
      });
  },
});

export default XFactorKpiSlice.reducer;
